import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Flame, Share2 } from "lucide-react";
import { ShareFlexCard } from "../components/ShareFlexCard";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

export default function HitTracker() {
  const { data: t } = useQuery({ queryKey: ["tracker"], queryFn: () => api.get("/hit-tracker").then((r) => r.data) });
  const { data: fishStreak } = useQuery({ queryKey: ["fishStreak"], queryFn: () => api.get("/fish-streak").then((r) => r.data) });
  const [showFlex, setShowFlex] = useState(false);
  if (!t) return <div className="text-neutral-500 font-data text-sm animate-pulse">Crunching results...</div>;

  const cards = [
    { label: "Season Record", value: `${t.record.wins}-${t.record.losses}`, sub: `${t.win_pct}% win rate`, color: "text-white" },
    { label: "Units Won", value: `${t.units > 0 ? "+" : ""}${t.units}u`, sub: `${t.roi}% ROI`, color: t.units >= 0 ? "text-[#22c55e]" : "text-red-400" },
    { label: "Month to Date", value: `${t.mtd.units > 0 ? "+" : ""}${t.mtd.units}u`, sub: `${t.mtd.wins}-${t.mtd.losses} MTD`, color: t.mtd.units >= 0 ? "text-[#22c55e]" : "text-red-400" },
    { label: "Today's Record", value: `${t.today.wins}-${t.today.losses}`, sub: "resets daily", color: "text-[#a855f7]" },
  ];

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Hit <span className="gold-text">Tracker</span></h1>
          <button onClick={() => setShowFlex(true)} data-testid="tracker-flex-btn"
            className="inline-flex items-center gap-1.5 border border-[#a855f7]/60 bg-[#a855f7]/10 text-[#a855f7] hover:bg-[#a855f7]/20 text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full transition-colors duration-200">
            <Share2 size={13} /> Flex the Record
          </button>
          {showFlex && (
            <ShareFlexCard
              record={{ wins: t.record?.wins || 0, losses: t.record?.losses || 0, units: t.units ?? 0 }}
              fishStreak={fishStreak}
              onClose={() => setShowFlex(false)}
            />
          )}
          {t.streak?.type === "W" && t.streak.count >= 2 && (
            <span className="inline-flex items-center gap-1.5 border border-[#a855f7]/60 bg-[#a855f7]/10 text-[#a855f7] text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full" data-testid="tracker-streak-badge">
              <Flame size={13} /> {t.streak.count} Pick Win Streak
            </span>
          )}
          {t.streak?.type === "L" && t.streak.count >= 3 && (
            <span className="inline-flex items-center gap-1.5 border border-white/20 text-neutral-400 text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded-full" data-testid="tracker-cold-badge">
              Cold spell · {t.streak.count} straight
            </span>
          )}
        </div>
        <p className="text-neutral-500 text-sm mt-1">Every pick graded. Full transparency — 1 unit per pick.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 fade-up-1">
        {cards.map((c) => (
          <div key={c.label} className="card-surface rounded-lg p-5" data-testid={`tracker-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{c.label}</div>
            <div className={`font-data text-4xl font-bold mt-2 ${c.color}`}>{c.value}</div>
            <div className="text-xs text-neutral-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="card-surface rounded-xl p-5 fade-up-2">
        <h3 className="font-display text-lg font-bold uppercase text-[#a855f7] mb-4">Cumulative Units — Last 30 Days</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={t.daily}>
              <defs>
                <linearGradient id="gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a855f7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#737373", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#737373", fontSize: 10 }} axisLine={false} tickLine={false} width={36} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#a3a3a3" }} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="cumulative" stroke="#a855f7" strokeWidth={2} fill="url(#gold)" name="Units" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-surface rounded-xl overflow-x-auto fade-up-3" data-testid="tracker-pick-history">
        <div className="px-4 pt-4">
          <h3 className="font-display text-lg font-bold uppercase text-[#a855f7]">Pick History</h3>
          <p className="text-xs text-neutral-500">The exact picks, lines and results — last 25 graded.</p>
        </div>
        <table className="w-full text-sm min-w-[620px] mt-2">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Date</th><th className="p-3">Player</th><th className="p-3">Bet</th><th className="p-3">Odds</th><th className="p-3">Type</th><th className="p-3">Result</th>
            </tr>
          </thead>
          <tbody className="font-data">
            {(t.recent || []).map((p, i) => (
              <tr key={i} className="border-b border-white/5" data-testid={`tracker-pick-${i}`}>
                <td className="p-3 font-sans text-neutral-400 whitespace-nowrap">{p.date?.slice(5)}</td>
                <td className="p-3 font-sans font-semibold">{p.player_name}</td>
                <td className="p-3 font-sans text-neutral-300">{p.bet}</td>
                <td className="p-3">{p.odds > 0 ? `+${p.odds}` : p.odds}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest font-bold text-neutral-400">{p.category}</span></td>
                <td className="p-3">
                  <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded ${p.result === "win" ? "bg-[#22c55e]/20 text-[#22c55e]" : "bg-red-500/20 text-red-400"}`}>
                    {p.result === "win" ? "W" : "L"}
                  </span>
                </td>
              </tr>
            ))}
            {(t.recent || []).length === 0 && <tr><td colSpan={6} className="p-4 text-neutral-600 font-sans text-center">No graded picks yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-surface rounded-xl overflow-x-auto fade-up-3">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Date</th><th className="p-3">Record</th><th className="p-3">Units</th><th className="p-3">Running Total</th>
            </tr>
          </thead>
          <tbody className="font-data">
            {[...t.daily].reverse().slice(0, 14).map((d) => (
              <tr key={d.date} className="border-b border-white/5">
                <td className="p-3 font-sans text-neutral-300">{d.date}</td>
                <td className="p-3">{d.wins}-{d.losses}</td>
                <td className={`p-3 font-bold ${d.units >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{d.units > 0 ? "+" : ""}{d.units}u</td>
                <td className={`p-3 ${d.cumulative >= 0 ? "text-[#22c55e]" : "text-red-400"}`}>{d.cumulative > 0 ? "+" : ""}{d.cumulative}u</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
